import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  palette,
  radius,
  spacing,
  shadow,
  transition,
  duration,
  easing,
} from '../../style-utils/theme';
import { fadeInUp, checkPop } from '../../style-utils/motion';
import { down } from '../../style-utils/media-queries';
import { SCALE } from '../../motion/tokens';

/**
 * Selectable option card. Resting: porcelain-white surface + hairline border.
 * Hover: iris border, soft iris wash, lift + shadow. Selected: iris border,
 * iris tint fill and a spring check affordance on the trailing edge.
 */
export const StyledButtonWrapper = styled.button<{ isSelected: boolean }>`
  position: relative;
  cursor: pointer;
  display: flex;
  box-sizing: border-box;
  width: 70%;
  /* NEVER NARROWER THAN A LABEL NEEDS, while the column allows it. 70% of the
     234px tablet column was 164px, and "Maintenance" came out as four
     fragments (2026-09-11). min() so it can never force the card wider than
     its own column. NO BACKTICKS IN THIS COMMENT. */
  min-width: min(100%, 280px);
  max-width: 420px;
  min-height: 56px;
  padding: ${spacing.sm};
  gap: ${spacing.xs};
  align-items: center;
  border-radius: ${radius.sm};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  margin-bottom: ${spacing.sm};
  color: ${palette.ink};
  font: inherit;
  text-align: inherit;
  transition: ${transition.colors}, transform ${duration.base} ${easing.spring};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;

  /* ONE press grammar across AppButton, NavButton and this card: lift 1px on
     hover, press to SCALE.pressed. This used to lift 2px and press to .98, so
     the option cards moved more than the buttons beside them. */
  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
    box-shadow: ${shadow.sm};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(${SCALE.pressed});
    transition-duration: ${duration.instant};
  }

  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled:not([aria-busy='true']) {
    opacity: 0.55;
  }

  &[aria-busy='true'] {
    cursor: progress;
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      border-color: ${palette.lagoon};
      background: ${palette.irisTint};
      box-shadow: ${shadow.sm};
    `}

  ${down('sm')} {
    width: 100%;
  }
`;

/* ── THE TWO SIDE SLOTS ARE FIXED; THE LABEL GETS THE REST ──
   They used to be flex: 1 like the label, so a card split into three EQUAL
   columns — an icon slot that is empty on every screen but the main menu, the
   label, and a check slot that is empty until selected — and the label only
   ever had a third of the card. That is what broke "Miscellaneous" and
   "Maintenance" mid-word at every width (2026-09-11). Equal fixed widths on
   both sides keep the label optically centred. */
export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
`;

/** Mirrors IconWrapper on the trailing edge: holds the selected check, or a
 *  count badge. A badge is wider than the check, so a badged slot sizes to it
 *  instead of clipping "12" inside a 20px box. */
export const TrailingSlot = styled.div<{ $hasBadge?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: ${({ $hasBadge }) => ($hasBadge ? '0 0 auto' : '0 0 20px')};
  width: ${({ $hasBadge }) => ($hasBadge ? 'auto' : '20px')};
  height: ${({ $hasBadge }) => ($hasBadge ? 'auto' : '20px')};
`;

export const TextWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  /* A LABEL IS NEVER CLIPPED, AND NEVER BROKEN MID-WORD WHEN IT FITS.
     NO BACKTICKS IN THIS COMMENT -- it lives inside a styled template literal,
     where one silently ends the string and the file stops parsing.

     min-width:0 lets the box shrink so an over-long label wraps inside it
     instead of pushing the card wider (reported 2026-08-13, when the tail of
     'Miscellaneous' was cut off).

     break-word, NOT anywhere. anywhere lets the browser break inside a word at
     ANY width, and with a narrow label column it always did. break-word breaks
     a word only when that word cannot fit on a line of its own, so every
     label that fits stays whole, and one that genuinely cannot still wraps
     rather than being clipped — these are a CLOSED vocabulary the desk selects
     by, so every character has to survive. */
  min-width: 0;
  text-align: center;
  overflow-wrap: break-word;

  /* The narrowest phones: 320px leaves the label about 118px at 18px bold,
     and "Miscellaneous" needs 123px. One step down keeps it on one line.
     & > span, NOT & > *: the universal selector adds no specificity, so it tied
     with the label's own font-size class and lost on source order (measured:
     still 18px at 320). The type selector wins the tie. */
  ${down('xs')} {
    & > span {
      font-size: 16px;
    }
  }
`;

/** Spring check affordance shown on the trailing edge of a selected card. */
export const SelectedCheck = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: ${radius.circle};
  background: ${palette.iris};
  box-shadow: ${shadow.brand};
  animation: ${checkPop} ${duration.base} ${easing.spring} both;

  &::after {
    content: '';
    width: 5px;
    height: 9px;
    margin-top: -2px;
    border: solid ${palette.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;
